import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Alert, App, Button, Input, Radio, Select, Skeleton, Space, Table } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { aiApi, type AiPingDto } from '../lib/wikiApi';

const MODEL_HINTS: Record<string, string> = {
  'claude-opus-5-5': 'лучшее качество текста',
  'claude-sonnet-5-5': 'быстрее и дешевле',
  'claude-haiku-4-5': 'самая дешёвая, для простых правок',
};

/**
 * «Администрирование → ИИ»: чем работает ИИ вики (GigaChat или Claude через
 * посредника в Казахстане — Anthropic блокирует IP РФ), модель, проверка
 * связи и расход за месяц. Ключ Claude — в Lockbox, здесь его нет.
 * «Claude» здесь — общий ИИ CRM: Claude или OpenAI по настройке «CRM → Настройки →
 * Интеграции → ИИ» (30.10.2026); при OpenAI модель Claude из этой формы не используется
 * (берётся модель OpenAI из CRM), а адрес посредника остаётся общий — задаётся здесь.
 */
export function AiSettingsSection(): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['wiki', 'ai-settings'], queryFn: () => aiApi.settings(), retry: false });
  const [provider, setProvider] = useState<'gigachat' | 'anthropic'>('gigachat');
  const [proxyUrl, setProxyUrl] = useState('');
  const [model, setModel] = useState('claude-opus-5-5');
  const [ping, setPing] = useState<AiPingDto | null>(null);

  useEffect(() => {
    if (!q.data) return;
    setProvider(q.data.settings.provider);
    setProxyUrl(q.data.settings.proxyUrl ?? '');
    setModel(q.data.settings.model);
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => aiApi.saveSettings({ provider, proxyUrl, model }),
    onSuccess: () => { message.success('Настройки ИИ сохранены'); void qc.invalidateQueries({ queryKey: ['wiki', 'ai-settings'] }); },
    onError: (e: Error) => message.error(e.message),
  });
  const test = useMutation({ mutationFn: () => aiApi.testConnection(), onSuccess: setPing, onError: (e: Error) => message.error(e.message) });

  if (q.isLoading) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (q.error) return <Alert type="error" showIcon message={(q.error as Error).message} />;
  const d = q.data!;
  const openai = d.aiProvider === 'openai';
  const aiName = openai ? 'OpenAI' : 'Claude';
  const modelOptions = Array.from(new Set([model, ...Object.keys(MODEL_HINTS), ...(ping?.models ?? [])])).map((m) => ({
    value: m,
    label: MODEL_HINTS[m] ? `${m} — ${MODEL_HINTS[m]}` : m,
  }));
  const dirty = provider !== d.settings.provider || proxyUrl.trim() !== (d.settings.proxyUrl ?? '') || model !== d.settings.model;

  return (
    <section style={{ border: '1px solid var(--border, #e8e6f0)', borderRadius: 8, padding: 16, display: 'grid', gap: 14 }}>
      <b style={{ fontSize: 15 }}>Чем работает ИИ вики</b>
      <Radio.Group id="ai-provider" value={provider} onChange={(e) => setProvider(e.target.value as 'gigachat' | 'anthropic')}>
        <Radio value="gigachat">GigaChat (напрямую из РФ)</Radio>
        <Radio value="anthropic">Claude или OpenAI — по общей настройке ИИ (через посредника)</Radio>
      </Radio.Group>
      {provider === 'anthropic' && (
        <div style={{ display: 'grid', gap: 10, maxWidth: 640 }}>
          {openai && <Alert type="info" showIcon message="В CRM выбран OpenAI («Настройки → Интеграции → ИИ»): вики отвечает через него, модель берётся оттуда. Адрес посредника общий — задаётся здесь. Поле «Модель» действует, только если вернуть Claude." />}
          {!d.claudeKeyConfigured && <Alert type="warning" showIcon message={openai ? 'Нет ключа OPENAI_API_KEY в Lockbox — OpenAI работать не будет' : 'Нет ключа ANTHROPIC_API_KEY в Lockbox — Claude работать не будет'} />}
          <label htmlFor="ai-proxy" style={{ fontSize: 13 }}>
            Адрес посредника
            <Input id="ai-proxy" value={proxyUrl} onChange={(e) => setProxyUrl(e.target.value)} placeholder="http://185.113.132.233:3128" style={{ marginTop: 4 }} />
          </label>
          <label htmlFor="ai-model" style={{ fontSize: 13 }}>
            Модель
            <Select id="ai-model" value={model} onChange={setModel} options={modelOptions} showSearch style={{ width: '100%', marginTop: 4 }} />
          </label>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Если Claude / OpenAI или посредник не отвечает, вики сама ответит через GigaChat. Поиск по смыслу всегда идёт через GigaChat.
          </div>
        </div>
      )}
      <Space wrap>
        <Button type="primary" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>Сохранить</Button>
        <Button loading={test.isPending} onClick={() => test.mutate()} title="Проверяет сохранённые настройки">Проверить связь с ИИ</Button>
      </Space>
      {ping && (ping.ok
        ? <Alert type="success" showIcon message={`${ping.provider === 'openai' ? 'OpenAI' : 'Claude'} отвечает за ${ping.ms} мс${ping.proxy ? ' через посредника' : ''}, ${ping.provider === 'openai' ? `модель ${ping.model ?? '—'}` : `доступно моделей: ${ping.models.length}`}`} />
        : <Alert type="error" showIcon message={`${ping.provider === 'openai' ? 'OpenAI' : 'Claude'} не отвечает: ${ping.error ?? 'нет ответа'}`} />)}

      <div>
        <div style={{ fontSize: 13, marginBottom: 6 }}>Расход ИИ ({aiName}) за {d.usage.month}</div>
        <Table
          size="small"
          rowKey="model"
          pagination={false}
          dataSource={d.usage.rows}
          locale={{ emptyText: 'Запросов к Claude / OpenAI в этом месяце не было' }}
          columns={[
            { title: 'Модель', dataIndex: 'model' },
            { title: 'Запросов', dataIndex: 'calls', align: 'right' },
            { title: 'Токенов на вход', dataIndex: 'inputTokens', align: 'right', render: (v: number) => v.toLocaleString('ru-RU') },
            { title: 'Токенов на выход', dataIndex: 'outputTokens', align: 'right', render: (v: number) => v.toLocaleString('ru-RU') },
          ]}
          scroll={{ x: 'max-content' }}
        />
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Деньги — в кабинете console.anthropic.com (Claude) или platform.openai.com (OpenAI); там же месячный лимит.</div>
      </div>
    </section>
  );
}
